import * as PIXI from 'pixi.js'

type TextStyleOptions = Record<string, unknown>

export interface OutlinedTextPalette {
    /** Gradient-filled copy of the word, drawn last. */
    fill: TextStyleOptions
    /** Flat copy behind it, stroked thickly enough to read as an outline. */
    outline: TextStyleOptions
    /** Optional flat copy between the two, offset down to read as an extruded bottom face. */
    bevel?: TextStyleOptions
}

export interface OutlinedTextLabelOptions {
    /** Clamped against the fill layer's own width, measured in this label's local space. */
    maxWidth: number
    /** Must match `dropShadow.distance` on the outline style — see the drift note below. */
    shadowDistance: number
    /** Horizontal squeeze, for art lettered in a condensed face. */
    squeezeX?: number
    /** How far the bevel copy sits below the glyphs. */
    bevelOffsetY?: number
}

/**
 * A word drawn as stacked copies of itself — outline, optional bevel, gradient fill — centred on its
 * own origin.
 *
 * This is what the game's baked word art looks like, and one text object cannot produce it: a stroke
 * is centred on the glyph outline, so half of its width is taken out of the fill and heavy lettering
 * comes out thin. Stacking instead keeps the fill at its design weight.
 */
export class OutlinedTextLabel extends PIXI.Container {
    private readonly glyphs = new PIXI.Container()
    private readonly outline: PIXI.Text
    private readonly bevel: PIXI.Text | null
    private readonly fill: PIXI.Text
    private readonly options: OutlinedTextLabelOptions

    constructor(palette: OutlinedTextPalette, options: OutlinedTextLabelOptions) {
        super()

        this.options = options

        this.outline = OutlinedTextLabel.createLayer(palette.outline)
        this.bevel = palette.bevel ? OutlinedTextLabel.createLayer(palette.bevel) : null
        this.fill = OutlinedTextLabel.createLayer(palette.fill)

        //## PixiJS budgets for a drop shadow by growing the measured box on the right and bottom only,
        //## so a centred anchor drifts half that distance up and left. Only the outline casts one —
        //## push it back by the same half to keep the copies of the word registered.
        this.outline.position.set(options.shadowDistance / 2, options.shadowDistance / 2)
        this.bevel?.position.set(0, options.bevelOffsetY ?? 0)

        this.glyphs.addChild(this.outline)
        if (this.bevel) {
            this.glyphs.addChild(this.bevel)
        }
        this.glyphs.addChild(this.fill)
        this.addChild(this.glyphs)
    }

    private static createLayer(style: TextStyleOptions): PIXI.Text {
        const layer = new PIXI.Text({ text: '', style: { ...style } as any })
        layer.anchor.set(0.5)

        return layer
    }

    /** Swaps the colours without rebuilding, for labels that have an enabled and a disabled look. */
    setPalette(palette: OutlinedTextPalette): void {
        this.outline.style = { ...palette.outline } as any
        this.fill.style = { ...palette.fill } as any

        if (this.bevel && palette.bevel) {
            this.bevel.style = { ...palette.bevel } as any
        }

        this.fitToMaxWidth()
    }

    get text(): string {
        return this.fill.text
    }

    set text(value: string) {
        if (this.fill.text === value) {
            return
        }

        this.outline.text = value
        this.fill.text = value
        if (this.bevel) {
            this.bevel.text = value
        }

        this.fitToMaxWidth()
    }

    /** Measured on the fill layer: the outline's stroke and shadow are not part of the design width. */
    private fitToMaxWidth(): void {
        const squeezeX = this.options.squeezeX ?? 1

        this.glyphs.scale.set(squeezeX, 1)

        const width = this.fill.width * squeezeX
        if (!(width > this.options.maxWidth)) {
            return
        }

        const fit = this.options.maxWidth / width
        this.glyphs.scale.set(squeezeX * fit, fit)
    }
}
